import {
  CURRENT_AIR_QUALITY_URL,
  CURRENT_WILDFIRE_INCIDENTS_URL,
  DEFAULT_ACTIVE_HURRICANES_SOURCE,
  DEFAULT_AIR_QUALITY_SOURCE,
  DEFAULT_ARCGIS_LAYER_SOURCE,
  DEFAULT_SENSOR_ALPHA_SOURCE,
  DEFAULT_WILDFIRE_INCIDENTS_SOURCE,
  isArcGisLayerSourcePreset,
  isSensorAlphaSourcePreset,
  isTropicalSourcePreset,
  type AttributeCondition,
  type OrgEventAutomationCondition,
  type OrgEventAutomationLogic,
  type OrgEventAutomationLogicBlock,
  type OrgEventAutomationLogicJoin,
  type OrgEventAutomationSource,
  type ResourceIntersectCondition,
  formatSpatialMatchPhrase,
  sanitizeProximityKm,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { normalizeWeatherServiceUrl } from './weatherFeatureServerCatalog';
import { feedDisplayName, isFeedUri } from './externalFeeds';

export type AddAutomationConditionMode = 'compound' | 'independent';

export type AutomationRuleLogicSlice = {
  logic: OrgEventAutomationLogic | string;
  source: OrgEventAutomationSource;
  conditions: OrgEventAutomationCondition[];
  logicBlocks: OrgEventAutomationLogicBlock[];
};

export function newConditionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `cond-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function sanitizeLogicBlocks(
  blocks: OrgEventAutomationLogicBlock[]
): OrgEventAutomationLogicBlock[] {
  const sanitized: OrgEventAutomationLogicBlock[] = [];
  blocks.forEach((block, index) => {
    if (block.type === 'condition') {
      if (!block.conditionId) return;
      sanitized.push({
        type: 'condition',
        conditionId: block.conditionId,
        joinWithPrevious:
          index === 0 ? undefined : (block.joinWithPrevious ?? 'and'),
      });
      return;
    }
    if (block.conditionIds.length === 0) return;
    const internalJoin: OrgEventAutomationLogicJoin =
      block.internalJoin === 'or' ? 'or' : 'and';
    sanitized.push({
      type: 'group',
      id: block.id,
      joinWithPrevious:
        index === 0 ? undefined : (block.joinWithPrevious ?? 'and'),
      internalJoin,
      conditionIds: [...block.conditionIds],
    });
  });
  return sanitized;
}

export function ensureConditionIds(
  conditions: OrgEventAutomationCondition[]
): OrgEventAutomationCondition[] {
  return conditions.map((condition) =>
    condition.id ? condition : { ...condition, id: newConditionId() }
  );
}

export function getConditionById(
  conditions: OrgEventAutomationCondition[],
  conditionId: string
): OrgEventAutomationCondition | undefined {
  return conditions.find((c) => c.id === conditionId);
}

export function resolveConditionSource(
  condition: OrgEventAutomationCondition,
  ruleSource: OrgEventAutomationSource
): OrgEventAutomationSource {
  return condition.source ?? ruleSource;
}

export function normalizeAutomationRuleLogic<
  T extends AutomationRuleLogicSlice,
>(rule: T): T {
  const conditions = ensureConditionIds(
    Array.isArray(rule.conditions) ? rule.conditions : []
  );

  if (Array.isArray(rule.logicBlocks) && rule.logicBlocks.length > 0) {
    const knownIds = new Set(
      conditions.map((c) => c.id).filter((id): id is string => Boolean(id))
    );
    const logicBlocks = sanitizeLogicBlocks(
      rule.logicBlocks
        .map((block) => {
          if (block.type === 'condition') {
            return knownIds.has(block.conditionId) ? block : null;
          }
          const conditionIds = block.conditionIds.filter((id) =>
            knownIds.has(id)
          );
          if (conditionIds.length === 0) return null;
          return { ...block, conditionIds };
        })
        .filter((b): b is OrgEventAutomationLogicBlock => b != null)
    );

    return {
      ...rule,
      conditions,
      logicBlocks,
      logic: deriveLogicFromBlocks({ ...rule, conditions, logicBlocks }),
    };
  }

  if (conditions.length === 0) {
    return { ...rule, conditions, logicBlocks: [] };
  }

  const join: OrgEventAutomationLogicJoin = rule.logic === 'any' ? 'or' : 'and';

  const logicBlocks: OrgEventAutomationLogicBlock[] =
    conditions.length === 1
      ? [{ type: 'condition', conditionId: conditions[0].id as string }]
      : [
          {
            type: 'group',
            id: newConditionId(),
            internalJoin: join,
            conditionIds: conditions.map((c) => c.id as string),
          },
        ];

  return {
    ...rule,
    conditions,
    logicBlocks: sanitizeLogicBlocks(logicBlocks),
    logic: join === 'or' ? 'any' : 'all',
  };
}

export function deriveLogicFromBlocks(
  rule: Pick<AutomationRuleLogicSlice, 'logicBlocks' | 'logic'>
): OrgEventAutomationLogic {
  const blocks = rule.logicBlocks ?? [];
  const topLevelJoins = blocks
    .slice(1)
    .map((block) => block.joinWithPrevious ?? 'and');

  if (topLevelJoins.length === 0) {
    const onlyBlock = blocks[0];
    if (onlyBlock?.type === 'group') {
      return onlyBlock.internalJoin === 'or' ? 'any' : 'all';
    }
    return 'all';
  }

  if (topLevelJoins.every((j) => j === 'and')) return 'all';
  if (topLevelJoins.every((j) => j === 'or')) return 'any';
  return 'any';
}

function formatConditionShort(
  condition: OrgEventAutomationCondition,
  ruleSource: OrgEventAutomationSource
): string {
  const source = resolveConditionSource(condition, ruleSource);
  const feed =
    feedDisplayName(source.serviceUrl) ||
    source.layerLabel?.trim() ||
    (isSensorAlphaSourcePreset(source)
      ? 'Sensor Alpha'
      : isTropicalSourcePreset(source)
        ? 'Tropical'
        : isArcGisLayerSourcePreset(source)
          ? 'ArcGIS layer'
          : source.serviceUrl?.trim() || 'ArcGIS');

  if (condition.kind === 'attribute') {
    const fieldLabel =
      source.fieldSnapshot?.alias?.trim() ||
      source.fieldSnapshot?.name ||
      condition.field;
    const op = condition.operator === '==' ? '=' : condition.operator;
    return `${feed}: ${fieldLabel} ${op} ${condition.value}`;
  }
  if (condition.kind === 'aor_intersect') {
    const selected = condition.aorIds?.length ?? 0;
    return `${feed}: ≥ ${condition.minCount} of ${selected} AOR${
      selected === 1 ? '' : 's'
    } ${formatSpatialMatchPhrase(condition.proximityKm)}`;
  }
  if (condition.resourceSetId != null) {
    const name = condition.resourceName?.trim() || 'the selected resource';
    return `${feed}: ${name} ${formatSpatialMatchPhrase(condition.proximityKm)}`;
  }
  return `${feed}: ≥ ${condition.minCount} resource(s) ${formatSpatialMatchPhrase(
    condition.proximityKm
  )}`;
}

export function formatAutomationRuleFormula(
  rule: AutomationRuleLogicSlice
): string {
  const normalized = normalizeAutomationRuleLogic(rule);
  if (normalized.logicBlocks.length === 0) {
    return 'Add at least one condition.';
  }

  const formatBlock = (block: OrgEventAutomationLogicBlock): string => {
    if (block.type === 'condition') {
      const condition = getConditionById(
        normalized.conditions,
        block.conditionId
      );
      return condition
        ? `[${formatConditionShort(condition, normalized.source)}]`
        : '[Unknown]';
    }
    const joiner = block.internalJoin === 'or' ? ' | ' : ' AND ';
    const inner = block.conditionIds
      .map((id) => {
        const condition = getConditionById(normalized.conditions, id);
        return condition
          ? formatConditionShort(condition, normalized.source)
          : 'Unknown';
      })
      .join(joiner);
    return `[${inner}]`;
  };

  return normalized.logicBlocks
    .map((block, index) => {
      const text = formatBlock(block);
      if (index === 0) return text;
      const join = block.joinWithPrevious ?? 'and';
      return `${join === 'and' ? ' AND ' : ' OR '}${text}`;
    })
    .join('');
}

/**
 * Prose counterpart to {@link formatAutomationRuleFormula}: same structure, but
 * lowercase "and"/"or" and no brackets, so it reads as a sentence. Parentheses
 * appear only where the nesting is genuinely ambiguous, meaning a
 * multi-condition group sitting alongside other top-level blocks.
 */
export function describeRuleConditions(rule: AutomationRuleLogicSlice): string {
  const normalized = normalizeAutomationRuleLogic(rule);
  if (normalized.logicBlocks.length === 0) return '';

  const needsParens = normalized.logicBlocks.length > 1;
  const conditionText = (id: string): string => {
    const condition = getConditionById(normalized.conditions, id);
    return condition
      ? formatConditionShort(condition, normalized.source)
      : 'an unknown condition';
  };

  const describeBlock = (block: OrgEventAutomationLogicBlock): string => {
    if (block.type === 'group' && block.internalJoin !== 'or') {
      const groupConditions = block.conditionIds
        .map((id) => getConditionById(normalized.conditions, id))
        .filter((condition): condition is OrgEventAutomationCondition =>
          Boolean(condition)
        );
      if (
        groupConditions.length > 1 &&
        groupConditions.every((condition) => isAirQualityCondition(condition))
      ) {
        const text = describeAirQualitySameMonitor(groupConditions);
        return needsParens ? `(${text})` : text;
      }
    }
    if (block.type === 'condition') return conditionText(block.conditionId);
    const joiner = block.internalJoin === 'or' ? ' or ' : ' and ';
    const inner = block.conditionIds.map(conditionText).join(joiner);
    return needsParens && block.conditionIds.length > 1 ? `(${inner})` : inner;
  };

  return normalized.logicBlocks
    .map((block, index) => {
      const text = describeBlock(block);
      if (index === 0) return text;
      const join = (block.joinWithPrevious ?? 'and') === 'or' ? 'or' : 'and';
      return `${join} ${text}`;
    })
    .join(' ');
}

/** The optional follow-on actions, summarized for the reads-as sentence. */
export type RuleNarrativeActions = {
  eventName?: string;
  /** Channel labels already resolved for display, e.g. ['Email', 'In-app']. */
  notifyChannels?: string[];
  notifyRecipientCount?: number;
  checklistItemCount?: number;
};

/**
 * One sentence describing what the rule does end to end, for the pinned summary
 * above the stepper. Reads as "When <conditions>, create ... , notify ... , and
 * attach ...".
 */
export function describeRule(
  rule: AutomationRuleLogicSlice,
  actions: RuleNarrativeActions = {}
): string {
  const conditions = describeRuleConditions(rule);
  if (!conditions) {
    return 'Add a condition to describe when this rule should run.';
  }

  const clauses: string[] = [];
  const eventName = actions.eventName?.trim();
  clauses.push(
    eventName ? `create an event named "${eventName}"` : 'create an event'
  );

  const channels = actions.notifyChannels ?? [];
  if (channels.length > 0) {
    const recipients = actions.notifyRecipientCount ?? 0;
    const who =
      recipients > 0
        ? `${recipients} recipient${recipients === 1 ? '' : 's'}`
        : 'no one yet';
    clauses.push(`notify ${who} by ${formatList(channels)}`);
  }

  const items = actions.checklistItemCount ?? 0;
  if (items > 0) {
    clauses.push(`attach ${items} response task${items === 1 ? '' : 's'}`);
  }

  return `When ${conditions}, ${formatList(clauses)}.`;
}

function formatList(parts: string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/**
 * A rule is compound when more than one condition feeds the formula: either
 * several top-level blocks joined by AND/OR, or one group joining its own
 * conditions.
 */
export function isCompoundRule(
  logicBlocks: OrgEventAutomationLogicBlock[]
): boolean {
  return (
    logicBlocks.length > 1 ||
    logicBlocks.some(
      (block) => block.type === 'group' && block.conditionIds.length > 1
    )
  );
}

export type FeedPreset =
  | 'tropical'
  | 'wildfire_incidents'
  | 'air_quality'
  | 'custom'
  | 'sensor_alpha'
  | 'arcgis_layer';

export function createDefaultCondition(
  preset: FeedPreset = 'custom'
): OrgEventAutomationCondition {
  const id = newConditionId();
  if (preset === 'tropical') {
    return {
      id,
      kind: 'resource_intersect',
      minCount: 1,
      geometryFrom: 'cone',
      source: { ...DEFAULT_ACTIVE_HURRICANES_SOURCE },
    };
  }
  if (preset === 'wildfire_incidents') {
    return {
      id,
      kind: 'resource_intersect',
      minCount: 1,
      geometryFrom: 'feature',
      source: { ...DEFAULT_WILDFIRE_INCIDENTS_SOURCE },
    };
  }
  if (preset === 'air_quality') {
    return {
      id,
      kind: 'attribute',
      field: 'PM25_AQI',
      operator: '>=',
      value: '150',
      source: { ...DEFAULT_AIR_QUALITY_SOURCE },
    };
  }
  if (preset === 'sensor_alpha') {
    return {
      id,
      kind: 'attribute',
      field: 'sensor_value',
      operator: '>=',
      value: '5',
      source: { ...DEFAULT_SENSOR_ALPHA_SOURCE },
    };
  }
  if (preset === 'arcgis_layer') {
    return {
      id,
      kind: 'attribute',
      field: '',
      operator: '==',
      value: '',
      source: { ...DEFAULT_ARCGIS_LAYER_SOURCE },
    };
  }
  return {
    id,
    kind: 'resource_intersect',
    minCount: 1,
    geometryFrom: 'feature',
    source: {
      type: 'arcgis_feature',
      preset: 'custom',
      serviceUrl: '',
      layerLabel: 'Custom ArcGIS layer',
    },
  };
}

export function addAutomationCondition(
  rule: AutomationRuleLogicSlice,
  options: {
    mode: AddAutomationConditionMode;
    join: OrgEventAutomationLogicJoin;
    preset?: FeedPreset;
  }
): AutomationRuleLogicSlice {
  const normalized = normalizeAutomationRuleLogic(rule);
  const newCondition = createDefaultCondition(options.preset ?? 'custom');
  const conditions = [...normalized.conditions, newCondition];
  let logicBlocks = [...normalized.logicBlocks];

  if (logicBlocks.length === 0) {
    return {
      ...normalized,
      conditions,
      logicBlocks: [
        { type: 'condition', conditionId: newCondition.id as string },
      ],
      logic: 'all',
    };
  }

  const lastBlockIndex = logicBlocks.length - 1;
  const lastBlock = logicBlocks[lastBlockIndex];

  if (options.mode === 'independent') {
    logicBlocks.push({
      type: 'condition',
      conditionId: newCondition.id as string,
      joinWithPrevious: options.join,
    });
  } else if (lastBlock.type === 'group') {
    logicBlocks[lastBlockIndex] = {
      ...lastBlock,
      internalJoin: options.join,
      conditionIds: [...lastBlock.conditionIds, newCondition.id as string],
    };
  } else {
    logicBlocks[lastBlockIndex] = {
      type: 'group',
      id: newConditionId(),
      joinWithPrevious: lastBlock.joinWithPrevious,
      internalJoin: options.join,
      conditionIds: [lastBlock.conditionId, newCondition.id as string],
    };
  }

  logicBlocks = sanitizeLogicBlocks(logicBlocks);
  return {
    ...normalized,
    conditions,
    logicBlocks,
    logic: deriveLogicFromBlocks({ logicBlocks, logic: normalized.logic }),
  };
}

export function removeAutomationCondition(
  rule: AutomationRuleLogicSlice,
  conditionId: string
): AutomationRuleLogicSlice {
  const normalized = normalizeAutomationRuleLogic(rule);
  if (normalized.conditions.length <= 1) {
    return normalized;
  }

  const conditions = normalized.conditions.filter((c) => c.id !== conditionId);
  const logicBlocks = sanitizeLogicBlocks(
    normalized.logicBlocks.flatMap((block): OrgEventAutomationLogicBlock[] => {
      if (block.type === 'condition') {
        return block.conditionId === conditionId ? [] : [block];
      }
      if (!block.conditionIds.includes(conditionId)) {
        return [block];
      }
      const remainingIds = block.conditionIds.filter(
        (id) => id !== conditionId
      );
      if (remainingIds.length === 0) return [];
      if (remainingIds.length === 1) {
        return [
          {
            type: 'condition',
            conditionId: remainingIds[0],
            joinWithPrevious: block.joinWithPrevious,
          },
        ];
      }
      return [{ ...block, conditionIds: remainingIds }];
    })
  );

  return {
    ...normalized,
    conditions,
    logicBlocks,
    logic: deriveLogicFromBlocks({ logicBlocks, logic: normalized.logic }),
  };
}

export function updateAutomationCondition(
  rule: AutomationRuleLogicSlice,
  conditionId: string,
  updater: (
    condition: OrgEventAutomationCondition
  ) => OrgEventAutomationCondition
): AutomationRuleLogicSlice {
  const normalized = normalizeAutomationRuleLogic(rule);
  return {
    ...normalized,
    conditions: normalized.conditions.map((condition) =>
      condition.id === conditionId ? updater(condition) : condition
    ),
  };
}

export function updateLogicBlockJoin(
  rule: AutomationRuleLogicSlice,
  blockIndex: number,
  join: OrgEventAutomationLogicJoin
): AutomationRuleLogicSlice {
  const normalized = normalizeAutomationRuleLogic(rule);
  const logicBlocks = sanitizeLogicBlocks(
    normalized.logicBlocks.map((block, index) =>
      index === blockIndex ? { ...block, joinWithPrevious: join } : block
    )
  );
  return {
    ...normalized,
    logicBlocks,
    logic: deriveLogicFromBlocks({ logicBlocks, logic: normalized.logic }),
  };
}

export function updateGroupInternalJoin(
  rule: AutomationRuleLogicSlice,
  groupId: string,
  join: OrgEventAutomationLogicJoin
): AutomationRuleLogicSlice {
  const normalized = normalizeAutomationRuleLogic(rule);
  const logicBlocks = normalized.logicBlocks.map((block) =>
    block.type === 'group' && block.id === groupId
      ? { ...block, internalJoin: join }
      : block
  );
  return {
    ...normalized,
    logicBlocks,
    logic: deriveLogicFromBlocks({ logicBlocks, logic: normalized.logic }),
  };
}

function isWildfireIncidentsSource(
  source: OrgEventAutomationSource | undefined
): boolean {
  if (!source) return false;
  if (source.preset === 'wildfire_incidents') return true;
  if (!source.serviceUrl) return false;
  return (
    normalizeWeatherServiceUrl(source.serviceUrl) ===
    normalizeWeatherServiceUrl(CURRENT_WILDFIRE_INCIDENTS_URL)
  );
}

function isAirQualitySource(
  source: OrgEventAutomationSource | undefined
): boolean {
  if (!source) return false;
  if (source.preset === 'air_quality') return true;
  if (!source.serviceUrl) return false;
  return (
    normalizeWeatherServiceUrl(source.serviceUrl) ===
    normalizeWeatherServiceUrl(CURRENT_AIR_QUALITY_URL)
  );
}

export function feedPresetFromSource(
  source: OrgEventAutomationSource | undefined
): FeedPreset {
  if (!source) return 'custom';
  if (isSensorAlphaSourcePreset(source)) return 'sensor_alpha';
  if (isTropicalSourcePreset(source)) return 'tropical';
  if (isWildfireIncidentsSource(source)) return 'wildfire_incidents';
  if (isAirQualitySource(source)) return 'air_quality';
  if (isArcGisLayerSourcePreset(source)) return 'arcgis_layer';
  return 'custom';
}

export function sourceForPreset(
  preset: FeedPreset,
  existing?: OrgEventAutomationSource
): OrgEventAutomationSource {
  if (preset === 'tropical') return { ...DEFAULT_ACTIVE_HURRICANES_SOURCE };
  if (preset === 'wildfire_incidents') {
    return { ...DEFAULT_WILDFIRE_INCIDENTS_SOURCE };
  }
  if (preset === 'air_quality') return { ...DEFAULT_AIR_QUALITY_SOURCE };
  if (preset === 'sensor_alpha') return { ...DEFAULT_SENSOR_ALPHA_SOURCE };
  if (preset === 'arcgis_layer') {
    if (existing?.preset === 'arcgis_layer') {
      return { ...existing };
    }
    return { ...DEFAULT_ARCGIS_LAYER_SOURCE };
  }
  if (existing?.preset === 'custom' && isFeedUri(existing.serviceUrl)) {
    return { ...existing };
  }
  return {
    type: 'arcgis_feature',
    preset: 'custom',
    serviceUrl: existing?.preset === 'custom' ? existing.serviceUrl || '' : '',
    layerLabel:
      existing?.preset === 'custom'
        ? existing.layerLabel || 'Custom ArcGIS layer'
        : 'Custom ArcGIS layer',
  };
}

const AIR_QUALITY_WITHIN_KM = 10;

function blockConditionIds(block: OrgEventAutomationLogicBlock): string[] {
  return block.type === 'condition'
    ? [block.conditionId]
    : [...block.conditionIds];
}

export function isAirQualityCondition(
  condition: OrgEventAutomationCondition | undefined
): boolean {
  return feedPresetFromSource(condition?.source) === 'air_quality';
}

/**
 * An AirNow block is one feed card: a single AirNow condition, or an AND group
 * of them.
 */
export function isAirQualityCardBlock(
  block: OrgEventAutomationLogicBlock,
  conditions: OrgEventAutomationCondition[]
): boolean {
  if (block.type === 'group' && block.internalJoin === 'or') return false;
  const ids = blockConditionIds(block);
  if (ids.length === 0) return false;
  return ids.every((id) =>
    isAirQualityCondition(conditions.find((condition) => condition.id === id))
  );
}

function isAirQualityNear(condition: OrgEventAutomationCondition): boolean {
  if (!isAirQualityCondition(condition)) return false;
  if (
    condition.kind !== 'resource_intersect' &&
    condition.kind !== 'aor_intersect'
  ) {
    return false;
  }
  return sanitizeProximityKm(condition.proximityKm) > 0;
}

function blockHasAirQualityPair(
  conditions: OrgEventAutomationCondition[]
): boolean {
  return (
    conditions.some(
      (condition) =>
        condition.kind === 'attribute' && isAirQualityCondition(condition)
    ) && conditions.some(isAirQualityNear)
  );
}

function airQualityNearCondition(): OrgEventAutomationCondition {
  return {
    id: newConditionId(),
    kind: 'resource_intersect',
    minCount: 1,
    geometryFrom: 'feature',
    proximityKm: AIR_QUALITY_WITHIN_KM,
    source: { ...DEFAULT_AIR_QUALITY_SOURCE },
  };
}

function describeAirQualitySameMonitor(
  conditions: OrgEventAutomationCondition[]
): string {
  const label =
    conditions[0]?.source?.layerLabel?.trim() || 'Current air quality (AirNow)';
  const parts = conditions.map((condition) => {
    if (condition.kind === 'attribute') {
      const op = condition.operator === '==' ? '=' : condition.operator;
      return `has ${condition.field} ${op} ${condition.value}`;
    }
    if (condition.kind === 'aor_intersect') {
      const km = sanitizeProximityKm(condition.proximityKm);
      const selected = condition.aorIds?.length ?? 0;
      const noun = `AOR${selected === 1 ? '' : 's'}`;
      return km > 0
        ? `is within ${km} km of at least ${condition.minCount} of ${selected} ${noun}`
        : `overlaps at least ${condition.minCount} of ${selected} ${noun}`;
    }
    if (
      condition.kind === 'resource_intersect' &&
      condition.resourceSetId != null
    ) {
      const name = condition.resourceName?.trim() || 'the selected resource';
      const km = sanitizeProximityKm(condition.proximityKm);
      return km > 0 ? `is within ${km} km of ${name}` : `overlaps ${name}`;
    }
    const km = sanitizeProximityKm(
      condition.kind === 'resource_intersect' ? condition.proximityKm : 0
    );
    const min =
      condition.kind === 'resource_intersect' ? condition.minCount : 1;
    const noun = `resource${min === 1 ? '' : 's'}`;
    return km > 0
      ? `is within ${km} km of at least ${min} ${noun}`
      : `overlaps at least ${min} ${noun}`;
  });
  return `the same ${label} monitor ${parts.join(' and ')}`;
}

/**
 * Turn the condition the author just pointed at AirNow into an AND group: PM2.5
 * AQI >= 150, and Resources near within 10 km. A second call keeps that pair.
 */
export function applyAirQualityFeed(
  rule: AutomationRuleLogicSlice,
  conditionId: string
): AutomationRuleLogicSlice {
  const normalized = normalizeAutomationRuleLogic(rule);
  const blockIndex = normalized.logicBlocks.findIndex((block) =>
    blockConditionIds(block).includes(conditionId)
  );
  if (blockIndex < 0) return normalized;

  const block = normalized.logicBlocks[blockIndex];
  const blockConditions = blockConditionIds(block)
    .map((id) => getConditionById(normalized.conditions, id))
    .filter((condition): condition is OrgEventAutomationCondition =>
      Boolean(condition)
    );
  if (blockHasAirQualityPair(blockConditions)) return normalized;

  const existing = getConditionById(normalized.conditions, conditionId);
  const attribute: OrgEventAutomationCondition =
    existing && existing.kind === 'attribute' && isAirQualityCondition(existing)
      ? existing
      : { ...createDefaultCondition('air_quality'), id: conditionId };
  const alreadyNear = blockConditions.some(isAirQualityNear);
  const near = alreadyNear ? null : airQualityNearCondition();

  let conditions = normalized.conditions.map((condition) =>
    condition.id === conditionId ? attribute : condition
  );
  if (near?.id) conditions = [...conditions, near];

  const siblingAirIds = blockConditionIds(block).filter((id) => {
    if (id === conditionId) return false;
    const sibling = getConditionById(conditions, id);
    return isAirQualityCondition(sibling);
  });
  const airIds = [conditionId, ...siblingAirIds];
  if (near?.id) airIds.push(near.id);
  const otherIds = blockConditionIds(block).filter(
    (id) => !airIds.includes(id)
  );

  const logicBlocks = [...normalized.logicBlocks];
  const airGroup: OrgEventAutomationLogicBlock = {
    type: 'group',
    id:
      block.type === 'group' && otherIds.length === 0
        ? block.id
        : newConditionId(),
    joinWithPrevious: otherIds.length === 0 ? block.joinWithPrevious : 'and',
    internalJoin: 'and',
    conditionIds: airIds,
  };

  if (otherIds.length === 0) {
    logicBlocks[blockIndex] = airGroup;
  } else {
    const remaining: OrgEventAutomationLogicBlock =
      otherIds.length === 1 || block.type !== 'group'
        ? {
            type: 'condition',
            conditionId: otherIds[0],
            joinWithPrevious: block.joinWithPrevious,
          }
        : {
            type: 'group',
            id: block.id,
            joinWithPrevious: block.joinWithPrevious,
            internalJoin: block.internalJoin,
            conditionIds: otherIds,
          };
    logicBlocks.splice(blockIndex, 1, remaining, airGroup);
  }

  const next = {
    ...normalized,
    conditions,
    logicBlocks: sanitizeLogicBlocks(logicBlocks),
  };
  return {
    ...next,
    logic: deriveLogicFromBlocks(next),
  };
}

/** Leave the AirNow card as one condition on the newly chosen feed. */
export function leaveAirQualityCard(
  rule: AutomationRuleLogicSlice,
  conditionIds: string[],
  preset: FeedPreset
): AutomationRuleLogicSlice {
  if (preset === 'air_quality') {
    const id = conditionIds[0];
    return id
      ? applyAirQualityFeed(rule, id)
      : normalizeAutomationRuleLogic(rule);
  }
  const normalized = normalizeAutomationRuleLogic(rule);
  const keepId = conditionIds[0];
  if (!keepId) return normalized;
  const replacement: OrgEventAutomationCondition = {
    ...createDefaultCondition(preset),
    id: keepId,
  };
  const drop = new Set(conditionIds.slice(1));
  const conditions = normalized.conditions
    .filter((condition) => !condition.id || !drop.has(condition.id))
    .map((condition) => (condition.id === keepId ? replacement : condition));
  const logicBlocks = sanitizeLogicBlocks(
    normalized.logicBlocks.flatMap((block): OrgEventAutomationLogicBlock[] => {
      const ids = blockConditionIds(block);
      if (!ids.some((id) => conditionIds.includes(id))) return [block];
      return [
        {
          type: 'condition',
          conditionId: keepId,
          joinWithPrevious: block.joinWithPrevious,
        },
      ];
    })
  );
  const next = { ...normalized, conditions, logicBlocks };
  return {
    ...next,
    logic: deriveLogicFromBlocks(next),
  };
}

export type { AttributeCondition, ResourceIntersectCondition };
